import React, { useEffect, useState } from "react";
import { useLocation, useOutletContext } from "react-router-dom";
import "../../styles/common/profile.scss";
import ChangePasswordPopup from "./changePasswordPopup";
import SuccessPopup from "./SuccessPopup";
import GlobalLoader from "./GlobalLoader";
import Cookies from "js-cookie";
import {
  getAdminProfile,
  getInstitutionProfile,
  updateAdminProfile,
  updateInstitutionProfile,
  changeAdminPassword,
  changeInstitutionPassword,
} from "../../helper/apicalls/profile";

const Profile = () => {
  const [profileData, setProfileData] = useState({
    name: "",
    email: "",
    phonenumber: "",
    role: "",
    institution: null,
  });

  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState({ ...profileData });
  const [showPasswordPopup, setShowPasswordPopup] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("myProfile");
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [popupType, setPopupType] = useState("success");

  const location = useLocation();
  const outletContext = useOutletContext();
  const setHeaderName = outletContext?.setHeaderName;
  const roleFromCookie = Cookies.get("role") || "";
  const normalizedRole = (profileData.role || roleFromCookie || "")
    .toString()
    .toLowerCase();

  const extractPhoneNumber = (data = {}) => {
    const fieldCandidates = [
      "phonenumber",
      "phone_number",
      "phoneNumber",
      "phone",
      "mobile",
      "mobile_number",
      "mobileNumber",
      "contact_number",
      "contactNumber",
    ];

    for (const field of fieldCandidates) {
      if (data[field]) {
        return data[field];
      }
    }

    const institutionData = data.institution || {};
    for (const field of fieldCandidates) {
      if (institutionData[field]) {
        return institutionData[field];
      }
    }

    return "";
  };

  const isAdmin = normalizedRole === "admin";
  const isInstitutionRoute = location.pathname.includes("institution");
  const shouldShowInstitutionTab = isInstitutionRoute;
  const shouldShowTabs = shouldShowInstitutionTab;

  const handleEditToggle = () => {
    if (isEditing) {
      setEditedData({ ...profileData });
    } else {
      setEditedData({ ...profileData });
    }
    setIsEditing(!isEditing);
  };

  const handleInputChange = (field, value) => {
    setEditedData({
      ...editedData,
      [field]: value,
    });
  };

  const handleSave = async () => {
    try {
      setIsLoading(true);
      const role = Cookies.get("role");
      let token = "";

      const payload = {
        name: editedData.name,
        email: editedData.email,
        phonenumber: editedData.phonenumber,
      };

      if (role === "admin") {
        token = Cookies.get("admin_token") || "";
        await updateAdminProfile(token, payload);
      } else {
        token = Cookies.get("institution_token") || "";
        await updateInstitutionProfile(token, payload);
      }

      setProfileData({ ...editedData });
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating profile:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePassword = async (passwordData) => {
    try {
      setIsLoading(true);
      const role = Cookies.get("role");
      let token = "";
      
      const payload = {
        oldPassword: passwordData.oldPassword,
        newPassword: passwordData.newPassword,
      };

      if (role === "admin") {
        token = Cookies.get("admin_token") || "";
        await changeAdminPassword(token, payload);
      } else {
        token = Cookies.get("institution_token") || "";
        await changeInstitutionPassword(token, payload);
      }

      setPopupMessage("Password changed successfully");
      setPopupType("success");
      setShowSuccessPopup(true);
      setShowPasswordPopup(false);
    } catch (error) {
      console.error("Error changing password:", error);
      setPopupMessage(error.message || "Failed to change password. Please try again.");
      setPopupType("error");
      setShowSuccessPopup(true);
    } finally {
      setIsLoading(false);
    }
  };

  const formatRole = (role) => {
    if (!role) return "";
    return role
      .toString()
      .toLowerCase()
      .split("_")
      .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
      .join(" ");
  };

  const fetchProfile = async () => {
    try {
      setIsLoading(true);
      const role = Cookies.get("role");
      let token = "";
      let response;

      if (role === "admin") {
        token = Cookies.get("admin_token") || "";
        response = await getAdminProfile(token);
      } else {
        token = Cookies.get("institution_token") || "";
        response = await getInstitutionProfile(token);
      }

      const data = response?.data || response || {};

      setProfileData({
        name: data.username || data.name || "",
        email: data.email || "",
        // phonenumber: extractPhoneNumber(data),
        role: data.role || role || "",
        institution: data.institution || null,
      });
      setEditedData({
        name: data.username || data.name || "",
        email: data.email || "",
        // phonenumber: extractPhoneNumber(data),
        role: data.role || role || "",
      });
    } catch (error) {
      console.error("Error fetching profile:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!shouldShowInstitutionTab && activeTab !== "myProfile") {
      setActiveTab("myProfile");
    }
  }, [shouldShowInstitutionTab, activeTab]);

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    if (setHeaderName) {
      setHeaderName("Profile");
    }
  }, [setHeaderName]);

  return (
    <div className="profile-dashboard">
      <GlobalLoader visible={isLoading} text="Loading..." />
      <div className="profile-content">
        <div className="profile-card">
          {shouldShowTabs && (
            <div className="profile-tabs">
              <button
                type="button"
                className={`profile-tab-button ${
                  activeTab === "myProfile" ? "active" : ""
                }`}
                onClick={() => setActiveTab("myProfile")}
              >
                My Profile
              </button>
              {shouldShowInstitutionTab && (
                <button
                  type="button"
                  className={`profile-tab-button ${
                    activeTab === "institution" ? "active" : ""
                  }`}
                  onClick={() => setActiveTab("institution")}
                >
                  Institution Details
                </button>
              )}
            </div>
          )}

          {activeTab === "myProfile" && (
            <>
              <h2 className="profile-heading">My Profile</h2>

              <div className="avatar-section">
                <div className="avatar-container">
                  <div className="avatar-letter">
                    {profileData.name ? profileData.name.charAt(0).toUpperCase() : "U"}
                  </div>
                  {/* <button className="camera-button">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M21 12.3198V17.25C21 17.8023 20.5523 18.25 20 18.25H4C3.44772 18.25 3 17.8023 3 17.25V6.75C3 6.19772 3.44772 5.75 4 5.75H12.5607"
                        stroke="#4169FF"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                      <circle
                        cx="15.5"
                        cy="10"
                        r="3"
                        stroke="#4169FF"
                        strokeWidth="1.5"
                      />
                      <circle
                        cx="17"
                        cy="8.5"
                        r="1.5"
                        stroke="white"
                        strokeWidth="1.5"
                      />
                    </svg>
                  </button> */}
                </div>
              </div>
              <div className="role-section">
                <p className="role-text">
                  Role : {formatRole(profileData.role)}
                </p>
              </div>

              <div className="section-header">
                <h3 className="section-title">Personal</h3>
                <button className="edit-link" onClick={handleEditToggle}>
                  {isEditing ? "Done" : "Edit"}
                </button>
              </div>

              <div className="profile-form">
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label" htmlFor="profile-name">
                      Name
                    </label>
                    <input
                      id="profile-name"
                      type="text"
                      className={`profile-input ${isEditing ? "editable" : ""}`}
                      value={
                        isEditing ? editedData.name : profileData.name
                      }
                      onChange={(e) =>
                        handleInputChange("name", e.target.value)
                      }
                      disabled={!isEditing}
                    />
                  </div>

                  {/* <div className="form-group">
                    <label className="form-label" htmlFor="profile-mobile">
                      Mobile
                    </label>
                    <div
                      className={`mobile-input-container ${
                        isEditing ? "editable" : "readonly"
                      }`}
                    >
                      <span className="mobile-prefix">
                        <span className="country-flag" aria-hidden="true">
                          🇮🇳
                        </span>
                        <span className="dial-code">+91</span>
                      </span>
                      <input
                        id="profile-mobile"
                        type="tel"
                        className="profile-input"
                        value={
                          isEditing
                            ? editedData.phonenumber
                            : profileData.phonenumber
                        }
                        onChange={(e) =>
                          handleInputChange("phonenumber", e.target.value)
                        }
                        disabled={!isEditing}
                      />
                    </div>
                  </div> */}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label" htmlFor="profile-email">
                      Email
                    </label>
                    <input
                      id="profile-email"
                      type="email"
                      className="profile-input"
                      value={profileData.email}
                      disabled={true}
                    />
                  </div>
                </div>

                {isEditing && (
                  <div className="form-actionss">
                    <button className="save-button" onClick={handleSave}>
                      Save
                    </button>
                  </div>
                )}
              </div>
              {!isEditing && (
                <div className="change-password-section">
                  <button
                    type="button"
                    className="change-password-button"
                    onClick={() => setShowPasswordPopup(true)}
                  >
                    Change Password
                  </button>
                </div>
              )}
            </>
          )}

          {activeTab === "institution" && shouldShowInstitutionTab && (
            <div className="institution-details">
              <h2 className="profile-heading">Institution Details</h2>
              {profileData.institution ? (
                <>
                  <div className="institution-basic-info">
                    <p>
                      <strong>Name:</strong> {profileData.institution.name}
                    </p>
                    <p>
                      <strong>Type:</strong> {profileData.institution.type}
                    </p>
                    <p>
                      <strong>Address:</strong>{" "}
                      {profileData.institution.address}
                    </p>
                    <p>
                      <strong>Mobile:</strong> {profileData.institution.mobile}
                    </p>
                    <p>
                      <strong>Website:</strong>{" "}
                      {profileData.institution.website_url}
                    </p>
                  </div>

                  {profileData.institution.description && (
                    <div className="institution-description">
                      <h3>Description</h3>
                      <p>{profileData.institution.description}</p>
                    </div>
                  )}

                  <div className="institution-permissions">
                    <h3>Permissions</h3>
                    <ul>
                      {(profileData.role === "admin"
                        ? profileData.institution.admin_permissions
                        : profileData.institution.teacher_permissions || []
                      ).map((perm) => (
                        <li key={perm.permission_type}>
                          <span className="permission-type">
                            {perm.permission_type}
                          </span>
                          <span className="permission-flags">
                            Read: {perm.read ? "Yes" : "No"}, Write:{" "}
                            {perm.write ? "Yes" : "No"}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              ) : (
                <p>No institution details available.</p>
              )}
            </div>
          )}
        </div>
      </div>

      {showPasswordPopup && (
        <ChangePasswordPopup
          isOpen={showPasswordPopup}
          onClose={() => setShowPasswordPopup(false)}
          onChangePassword={handleChangePassword}
        />
      )}

      <SuccessPopup
        open={showSuccessPopup}
        onClose={() => setShowSuccessPopup(false)}
        message={popupMessage}
        type={popupType}
      />
    </div>
  );
};

export default Profile;
